import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { MagnifierIcon } from '../../components/Icons';

import requestClient from '../../utils/requestClient';
import Pagination from '../../components/Elements/Pagination';
import FormInput from '../../components/Elements/Form/FormInput';
import FormFile from '../../components/Elements/Form/FormFile';
import Modal from '../../components/Elements/Modal';
import SuccessMessage from '../../components/Elements/SuccessMessage';
import ErrorMessage from '../../components/Elements/ErrorMessage';

const Users = ({ }) => {
    const location = useLocation();
    const [success, setSuccess] = useState(location.state?.successMessage || false);
    const [errors, setErrors] = useState(null);
    const [data, setData] = useState(null);
    const [company, setCompany] = useState(null);
    const [currentPage, setCurrentPage] = useState(1)
    const [term, setTerm] = useState('')
    const modalRef = useRef(null)

    const getUsers = async () => {
        const response = await requestClient().get(`/api/v2/employer/employees/?page=${currentPage}&term=${term}`)
        setData(response.data);
    }

    const toggleModal = () => {
        modalRef.current.toggleModal()
    }

    useEffect(() => {
        async function getData() {
            await getUsers()
        }
        getData();
    }, []);

    useEffect(() => {
        async function paginate() {
            await getUsers()
        }
        paginate()
    }, [currentPage]);

    useEffect(() => {
        async function search() {
            await getUsers()
        }
        search()
    }, [term]);

    useEffect(() => {
        async function getCompany() {
            const response = await requestClient().get('/api/v2/employer/company/')

            setCompany(response.data.data);
        }
        getCompany();
    }, []);

    const inviteUser = async (id, email) => {
        setSuccess(false)
        setErrors(null)

        const response = await requestClient().post(`/api/v2/employer/employees/invite`, {
            'ids': [id]
        })
            .then(response => {
                setSuccess(`${email} has been invited`)
            })
            .catch(error => {
                setErrors(error.response.data.error)
            })
    };

    const inviteUsers = async () => {
        setSuccess(false)
        setErrors(null)

        const response = await requestClient().post(`/api/v2/employer/employees/invite`, {
            'ids': []
        })
            .then(response => {
                setSuccess(`Invites have been re-sent to members`)
            })
            .catch(error => {
                setErrors(error.response.data.error)
            })
    };

    const deleteUser = async (id) => {
        const confirmation = window.confirm(`Are you sure you want to delete this user?`);
        if (confirmation) {
            const response = await requestClient().delete(`/api/v2/employer/employees/${id}`)
            await getUsers()
        }
    };

    const uploadUser = async (event) => {
        setSuccess(false)
        setErrors(null)
        event.preventDefault()

        let formData = new FormData();
        formData.append("csv", event.target[0].files[0]);

        await requestClient().post(`/api/v2/employer/employees/invite/csv`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }
        ).then(response => {
            setSuccess(`Your invites are being processed`)
            toggleModal()
        }).catch(error => {
            setErrors(error.response.data.error)
            toggleModal()
        })
    };

    const Form = () => {
        return (
            <div>
                <h3 className='mb-6'>Upload</h3>
                <form onSubmit={uploadUser} className='grid gap-2 mb-6'>
                    <FormFile id="file" name="csv" label="Bulk upload users from a .csv file" props={{ 'required': 'required' }} />
                    <div className='flex justify-between'>
                        <a className="block font-bold mb-2" href="/assets/user-upload-example.csv" download={true}>Download Example CSV</a>
                    </div>
                    <button className='btn btn-invert w-full'>Import</button>
                </form>
                <p className='text-sm'>*<span className='font-bold'>{company.remaining_seats}</span> places remain in your plan. 
                Please <a href='mailto:hello@myopti.me' className='font-bold'>contact the OptiMe Team</a> to upgrade your subscription if you require more.</p>
            </div>
        )
    }

    const showLocation = !!(company?.locations?.length);
    const showDepartment = !!(company?.departments?.length);

    return (data ?
        <main>
            <section className="bg-white">
                <div className='container py-8 lg:py-12'>
                    <div className='border-b pb-8 flex lg:items-center lg:justify-between mb-8 gap-4 flex-col lg:flex-row'>
                        <h1>Users</h1>
                        <div className='rounded-full border flex relative overflow-hidden pl-[40px] w-full flex lg:w-auto'>
                            <MagnifierIcon className="absolute left-3 top-1/2 mt-[-10px]" />
                            <input type="text" placeholder="Search" className="border-none w-full px-1 py-3 focus:ring-0 min-w-[200px]" onKeyUp={(event) => { setTerm(event.target.value) }} />
                        </div>
                    </div>
                    {success ? <SuccessMessage title="Success" text={success} className="mb-4 sticky top-2" /> : null}
                    {errors ? <ErrorMessage title="Something is not right" errors={errors} className="mb-4 sticky top-2" /> : null}
                    <div className='flex gap-4 flex-col lg:flex-row lg:justify-end mb-4'>
                        <button className='btn btn-invert w-full lg:w-auto' onClick={() => inviteUsers()}>Resend Invites</button>
                        <Link to="/employer/users/create">
                            <button className='btn btn-invert w-full lg:w-auto'>Create</button>
                        </Link>
                        <Modal ref={modalRef} buttonText="Upload" slot={(<Form />)} />
                    </div>
                    <div className='overflow-scroll mb-8'>
                        <table className='table-auto w-full text-left'>
                            <thead className='text-sm text-purple uppercase'>
                                <tr>
                                    <th className='p-3'>User</th>
                                    <th className='p-3'>User Type</th>
                                    {showLocation && (<th className='p-3'>Location</th>)}
                                    {showDepartment && (<th className='p-3'>Department</th>)}
                                    <th className='p-3'>Onboarded</th>
                                    <th className='p-3'>&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.map((user, index) => (
                                    <tr key={user.id}>
                                        <td className={`p-3 px-3 font-bold ${index % 2 ? '' : 'bg-yellow-light'}`}>
                                            <Link to={`/employer/users/${user.id}/edit`} className="text-purple hover:underline">
                                                {user.email}
                                            </Link>
                                            <span className='block font-normal'>{user.first_name} {user.last_name}</span>
                                        </td>
                                        <td className={`p-3 ${index % 2 ? '' : 'bg-yellow-light'} capitalize`}>{user.user_type}</td>
                                        {showLocation && (
                                            <td className={`p-3 ${index % 2 ? '' : 'bg-yellow-light'}`}>
                                            {user.location?.name || '—'}
                                            </td>
                                        )}

                                        {showDepartment && (
                                            <td className={`p-3 ${index % 2 ? '' : 'bg-yellow-light'}`}>
                                            {user.department?.name || '—'}
                                            </td>
                                        )}
                                        <td className={`p-3 ${index % 2 ? '' : 'bg-yellow-light'}`}>{user.completed_onboarding ? 'Yes' : 'No'}</td>
                                        <td className={`p-3 ${index % 2 ? '' : 'bg-yellow-light'}`}>
                                            <span className='flex gap-2 justify-end flex-col lg:flex-row'>
                                                <button className="flex-none whitespace-nowrap btn-sm btn-invert w-auto" onClick={() => inviteUser(user.id, user.email)}>
                                                    RE-INVITE
                                                </button>
                                                <button className="flex-none whitespace-nowrap btn-sm btn-red" onClick={() => deleteUser(user.id)}>
                                                    DELETE
                                                </button>
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <Pagination currentPage={currentPage} data={data.data} pagination={data.meta.paginator} callback={setCurrentPage} />
                </div>
            </section>
        </main>
        : null);
}

export default Users;
